export type FeedbackSelection = {
  el: Element
  feedbackId: string | null
  selector: string
  xpath: string
  tag: string
  textPreview: string
  outerSnippet: string
}

const SNAP_SELECTOR =
  'section,[data-feedback-id],article,main>*,header,footer,nav,button,a,img,h1,h2,h3,[role="article"],[role="region"]'

export function cssPath(el: Element): string {
  if (el.id) return `#${CSS.escape(el.id)}`
  const feedbackId = el.getAttribute('data-feedback-id')
  if (feedbackId) return `[data-feedback-id="${CSS.escape(feedbackId)}"]`
  const parts: string[] = []
  let cur: Element | null = el
  while (cur && cur.nodeType === 1 && parts.length < 5) {
    let part = cur.tagName.toLowerCase()
    const parentEl: Element | null = cur.parentElement
    if (parentEl) {
      const siblings = Array.from(parentEl.children).filter(
        (c): c is Element => c.tagName === cur!.tagName,
      )
      if (siblings.length > 1) {
        part += `:nth-of-type(${siblings.indexOf(cur) + 1})`
      }
    }
    parts.unshift(part)
    cur = parentEl
    if (cur?.tagName === 'BODY' || cur?.tagName === 'HTML') break
  }
  return parts.join(' > ')
}

export function xpathOf(el: Element): string {
  const parts: string[] = []
  let cur: Element | null = el
  while (cur && cur.nodeType === 1) {
    let ix = 1
    let sib = cur.previousElementSibling
    while (sib) {
      if (sib.tagName === cur.tagName) ix++
      sib = sib.previousElementSibling
    }
    parts.unshift(`${cur.tagName.toLowerCase()}[${ix}]`)
    cur = cur.parentElement
    if (!cur || cur.tagName === 'HTML') break
  }
  return '/' + parts.join('/')
}

export function resolveTarget(start: Element | null): Element | null {
  if (!start || start === document.documentElement || start === document.body) return null
  let cur: Element | null = start
  while (cur && cur !== document.body) {
    if (cur.closest('[data-klfb-root]')) return null
    if (cur.matches(SNAP_SELECTOR)) return cur
    cur = cur.parentElement
  }
  return start
}

export function toSelection(el: Element): FeedbackSelection {
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 120)
  return {
    el,
    feedbackId: el.getAttribute('data-feedback-id'),
    selector: cssPath(el),
    xpath: xpathOf(el),
    tag: el.tagName.toLowerCase(),
    textPreview: text,
    outerSnippet: el.outerHTML.replace(/\s+/g, ' ').slice(0, 400),
  }
}

export type InspectHandlers = {
  /** Markier-Modus aktiv (FAB getippt) */
  isMarking: () => boolean
  /** Session noch aktiv */
  isArmed: () => boolean
  onSelect: (sel: FeedbackSelection | null) => void
  onOpenSheet: (sel: FeedbackSelection) => void
}

export function mountInspect(_shadowHost: HTMLElement, handlers: InspectHandlers): {
  stop: () => void
  clearSelection: () => void
  paint: (el: Element | null) => void
} {
  let selected: FeedbackSelection | null = null

  const outline = document.createElement('div')
  outline.setAttribute('data-klfb-outline', '1')
  Object.assign(outline.style, {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: '2147483646',
    border: '2px solid #6D28D9',
    boxShadow: '0 0 0 2px rgba(109,40,217,0.25)',
    borderRadius: '4px',
    display: 'none',
    transition: 'top 80ms, left 80ms, width 80ms, height 80ms',
  })
  document.body.appendChild(outline)

  const paint = (el: Element | null) => {
    if (!el) {
      outline.style.display = 'none'
      return
    }
    const r = el.getBoundingClientRect()
    outline.style.display = 'block'
    outline.style.top = `${Math.max(0, r.top)}px`
    outline.style.left = `${Math.max(0, r.left)}px`
    outline.style.width = `${r.width}px`
    outline.style.height = `${r.height}px`
  }

  const clearSelection = () => {
    selected = null
    paint(null)
    handlers.onSelect(null)
  }

  const selectAndOpen = (el: Element) => {
    const resolved = resolveTarget(el)
    if (!resolved) return
    selected = toSelection(resolved)
    paint(resolved)
    handlers.onSelect(selected)
    handlers.onOpenSheet(selected)
  }

  const onClick = (e: MouseEvent) => {
    if (!handlers.isArmed() || !handlers.isMarking()) return
    const t = e.target as Element | null
    if (t?.closest?.('[data-klfb-root]')) return
    const target = resolveTarget(t)
    if (!target) return
    e.preventDefault()
    e.stopPropagation()
    selectAndOpen(target)
  }

  const onScroll = () => paint(selected?.el ?? null)

  document.addEventListener('click', onClick, true)
  window.addEventListener('scroll', onScroll, true)
  window.addEventListener('resize', onScroll)

  return {
    stop: () => {
      outline.remove()
      document.removeEventListener('click', onClick, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    },
    clearSelection,
    paint,
  }
}
